import { Fragment, useState, useEffect } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { portalListCustomers, portalGetCustomer } from '../../lib/portalApi'

interface CustomerSummary {
  appUserId: string
  email: string | null
  displayName: string | null
  entitlements: Record<string, any>
  createdAtMs: number | null
  updatedAtMs: number | null
}

interface CustomerDetail {
  customer: any
  events: any[]
}

function activeEntitlementCount(entitlements: Record<string, any>): number {
  return Object.values(entitlements ?? {}).filter((e: any) => e?.isActive === true).length
}

/** Hạn xa nhất trong các gói còn hiệu lực — để biết PRO tới bao giờ. */
function proUntil(entitlements: Record<string, any>): number | null {
  const times = Object.values(entitlements ?? {})
    .filter((e: any) => e?.isActive === true)
    .map((e: any) => (typeof e?.expiresAtMs === 'number' ? e.expiresAtMs : null))
    .filter((v): v is number => v !== null)
  return times.length ? Math.max(...times) : null
}

function formatDate(ms: number | null): string {
  if (!ms) return '—'
  return new Date(ms).toLocaleDateString()
}

function formatDateTime(ms: number | null): string {
  if (!ms) return '—'
  return new Date(ms).toLocaleString()
}

export function CustomersView({ appId, tenantId }: { appId: string; tenantId: string }) {
  const { user } = useAuth()

  const [customers, setCustomers] = useState<CustomerSummary[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<Record<string, CustomerDetail>>({})
  const [detailLoading, setDetailLoading] = useState<string | null>(null)

  const fetchCustomers = async (cursor?: string) => {
    if (!user) return
    try {
      setError(null)
      const idToken = await user.getIdToken()
      const resp = await portalListCustomers({ tenantId, appId, idToken, limit: 50, startAfter: cursor })
      setCustomers((prev) => cursor ? [...prev, ...resp.customers] : resp.customers)
      setNextCursor(resp.nextCursor)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setLoading(true)
    setCustomers([])
    setNextCursor(null)
    fetchCustomers()
  }, [appId, tenantId])

  const handleRowClick = async (appUserId: string) => {
    if (expandedId === appUserId) {
      setExpandedId(null)
      return
    }
    setExpandedId(appUserId)
    if (detail[appUserId]) return
    if (!user) return
    setDetailLoading(appUserId)
    try {
      const idToken = await user.getIdToken()
      const resp = await portalGetCustomer({ tenantId, appId, appUserId, idToken })
      setDetail((prev) => ({ ...prev, [appUserId]: resp }))
    } catch (err: any) {
      setDetail((prev) => ({ ...prev, [appUserId]: { customer: null, events: [] } }))
    } finally {
      setDetailLoading(null)
    }
  }

  const filtered = customers.filter((c) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      c.appUserId.toLowerCase().includes(q) ||
      (c.email ?? '').toLowerCase().includes(q) ||
      (c.displayName ?? '').toLowerCase().includes(q)
    )
  })

  if (loading) {
    return <div className="text-on-surface-variant font-body-sm p-xl">Loading customers...</div>
  }

  return (
    <div className="space-y-lg max-w-5xl">
      {error && (
        <div className="p-md bg-error-container text-on-error-container rounded-lg border border-error/20 font-body-sm">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-md flex-wrap">
        <div>
          <h3 className="font-headline-md text-[20px] font-semibold text-on-surface">Customers</h3>
          <p className="font-body-sm text-[13px] text-on-surface-variant mt-0.5">{customers.length} loaded</p>
        </div>
        <input
          type="text"
          placeholder="Search by user ID or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-sm bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-outline-variant rounded-xl text-on-surface-variant font-body-sm gap-2">
          <span className="material-symbols-outlined text-[40px]">group</span>
          <span>{search ? 'No customers match your search.' : 'No customers yet.'}</span>
        </div>
      ) : (
        <div className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-outline-variant bg-surface-container-low">
                <th className="text-left px-lg py-sm font-label-sm text-[12px] text-on-surface-variant font-semibold uppercase tracking-wide">User ID</th>
                <th className="text-left px-lg py-sm font-label-sm text-[12px] text-on-surface-variant font-semibold uppercase tracking-wide">Email</th>
                <th className="text-left px-lg py-sm font-label-sm text-[12px] text-on-surface-variant font-semibold uppercase tracking-wide">Active</th>
                <th className="text-left px-lg py-sm font-label-sm text-[12px] text-on-surface-variant font-semibold uppercase tracking-wide">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <Fragment key={c.appUserId}>
                  <tr
                    onClick={() => handleRowClick(c.appUserId)}
                    className="border-b border-outline-variant/50 hover:bg-surface-container-low cursor-pointer transition-colors"
                  >
                    <td className="px-lg py-md font-mono text-[13px] text-on-surface truncate max-w-[200px]">{c.appUserId}</td>
                    <td className="px-lg py-md font-body-sm text-[13px] text-on-surface-variant">{c.email ?? '—'}</td>
                    <td className="px-lg py-md">
                      {activeEntitlementCount(c.entitlements) > 0 ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-[#15803d]/10 text-[#15803d] border border-[#15803d]/20 font-label-sm text-[12px] font-bold">
                            PRO
                          </span>
                          {proUntil(c.entitlements) && (
                            <span className="font-label-sm text-[11px] text-on-surface-variant">
                              tới {formatDateTime(proUntil(c.entitlements)!)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-surface-container-high text-on-surface-variant font-label-sm text-[12px]">
                          free
                        </span>
                      )}
                    </td>
                    <td className="px-lg py-md font-body-sm text-[13px] text-on-surface-variant">{formatDate(c.createdAtMs)}</td>
                  </tr>

                  {/* Expanded detail row */}
                  {expandedId === c.appUserId && (
                    <tr className="bg-surface-container-low border-b border-outline-variant">
                      <td colSpan={4} className="px-lg py-lg">
                        {detailLoading === c.appUserId ? (
                          <div className="text-on-surface-variant font-body-sm">Loading details...</div>
                        ) : detail[c.appUserId] ? (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-lg">
                            {/* Entitlements */}
                            <div>
                              <h4 className="font-label-md text-[13px] font-semibold text-on-surface mb-sm">Entitlements</h4>
                              {Object.keys(detail[c.appUserId].customer?.entitlements ?? {}).length === 0 ? (
                                <p className="text-on-surface-variant font-body-sm text-[13px]">No entitlements.</p>
                              ) : (
                                <div className="space-y-sm">
                                  {Object.entries(detail[c.appUserId].customer?.entitlements ?? {}).map(([key, val]: [string, any]) => (
                                    <div key={key} className="p-sm bg-surface-container-lowest rounded-lg border border-outline-variant/50">
                                      <div className="flex items-center gap-sm mb-1">
                                        <span className="font-label-sm text-[12px] font-semibold text-on-surface font-mono">{key}</span>
                                        <span className={`inline-block px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${val?.isActive ? 'bg-[#15803d]/10 text-[#15803d]' : 'bg-surface-container-high text-on-surface-variant'}`}>
                                          {val?.isActive ? 'active' : 'expired'}
                                        </span>
                                      </div>
                                      {val?.productId && <div className="font-mono text-[11px] text-on-surface-variant">Product: {val.productId}</div>}
                                      {val?.expiresAtMs && <div className="font-mono text-[11px] text-on-surface-variant">Expires: {formatDateTime(val.expiresAtMs)}</div>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Subscription Events */}
                            <div>
                              <h4 className="font-label-md text-[13px] font-semibold text-on-surface mb-sm">Recent Events</h4>
                              {detail[c.appUserId].events.length === 0 ? (
                                <p className="text-on-surface-variant font-body-sm text-[13px]">No events recorded.</p>
                              ) : (
                                <div className="space-y-1 max-h-[280px] overflow-auto pr-1">
                                  {detail[c.appUserId].events.map((ev) => (
                                    <div key={ev.id} className="flex items-center justify-between p-sm bg-surface-container-lowest rounded-lg border border-outline-variant/50 gap-sm">
                                      <div className="min-w-0">
                                        <div className="font-label-sm text-[12px] font-semibold text-on-surface truncate">{ev.eventType ?? '—'}</div>
                                        <div className="font-mono text-[11px] text-on-surface-variant truncate">{ev.productId ?? '—'}</div>
                                      </div>
                                      <div className="text-right shrink-0">
                                        {ev.priceUsd != null && (
                                          <div className="font-label-sm text-[12px] text-on-surface font-semibold">${ev.priceUsd.toFixed(2)}</div>
                                        )}
                                        <div className="font-mono text-[11px] text-on-surface-variant">{formatDate(ev.createdAtMs)}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-on-surface-variant font-body-sm">Failed to load details.</div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Load more */}
      {nextCursor && (
        <div className="flex justify-center">
          <button
            onClick={() => fetchCustomers(nextCursor)}
            className="px-lg py-sm font-label-md text-[14px] bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high rounded-lg transition-colors"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  )
}
